<!--
  ONE SHELF, EITHER KIND — `/movies` and `/shows`, and `/movies/Comedy` for one
  genre of one kind. There is no second copy of this screen: the only difference
  between films and series is the word `movie` or `series` on the wire and three
  extra genres that only exist on television.

  Ordered the way the owner asked for it: POPULAR first, then NEW, then BY GENRE.
  Popular is the hero and the first rail, because it is the answer to "what is
  everyone watching" and that is the question most evenings start with.

  Every rail below the fold is a `LazyRail`: nineteen genres eagerly fetched is
  2.5 MB of JSON parsed on a phone before anything has been looked at.
-->
<script lang="ts">
  import type { CatalogItem } from '$types';
  import Button from '$lib/ui/Button.svelte';
  import Chip from '$lib/ui/Chip.svelte';
  import Empty from '$lib/ui/Empty.svelte';
  import Hero from '$lib/ui/Hero.svelte';
  import LazyRail from '$lib/ui/LazyRail.svelte';
  import Poster from '$lib/ui/Poster.svelte';
  import TileGrid from '$lib/ui/TileGrid.svelte';
  import Skeleton from '$lib/ui/Skeleton.svelte';
  import {
    catalog,
    catalogDeep,
    CATALOG_PAGE,
    CATALOG_PAGE_SHORT,
    genresFor,
    isGenre
  } from '$lib/ui/cinemeta';
  import { MORE_RETRY_MS } from '$lib/ui/rail-ring';
  import { browseHref, collectionHref, go, nav } from '$lib/ui/nav.svelte';
  import { scrolled, trackScrolled } from '$lib/ui/scrolled.svelte';
  import PlateRail from '$lib/ui/PlateRail.svelte';
  import { browseRails, daySeed, heroPicks } from '$lib/ui/shelf';
  import { COLLECTIONS } from '$lib/ui/collections';
  import { collectionArt, genreArt } from '$lib/ui/browse-art';

  const route = $derived(nav.route.name === 'browse' ? nav.route : null);
  const kind = $derived(route?.kind ?? 'movie');
  const genre = $derived(route?.genre ?? null);
  const heading = $derived(kind === 'series' ? 'Shows' : 'Movies');
  const genres = $derived(genresFor(kind));

  /* ── The shelf head: popular, which is also the hero ───────────────────── */

  let top = $state<CatalogItem[]>([]);
  let topFailed = $state(false);
  /** The head request ANSWERED (even with nothing): an empty answer is a fact
   *  to state, not a skeleton to shimmer behind for ever. */
  let topAnswered = $state(false);
  /** Bumped by "Try again", which is the whole of its job: it is a dependency of the
   *  effect below and of nothing else, so incrementing it re-asks the question. */
  let attempt = $state(0);

  $effect(() => {
    const k = kind;
    const g = genre;
    void attempt;
    const abort = new AbortController();
    top = [];
    topFailed = false;
    topAnswered = false;

    void catalog({ kind: k, catalog: 'top', ...(g ? { genre: g } : {}) }, abort.signal)
      .then((items) => {
        if (abort.signal.aborted) return;
        top = items;
        topAnswered = true;
      })
      .catch((err: unknown) => {
        if ((err as Error)?.name === 'AbortError') return;
        topFailed = true;
      });

    return () => abort.abort();
  });

  /*
   * The banner is the same slideshow Home draws, over this shelf's own popular
   * list (item 10). It does NOT consume the first title: with five slides,
   * hiding all five from the grid below would punch a hole in the top of the
   * genre, and a banner is a banner rather than the first cell of the grid.
   */
  const featured = $derived(heroPicks([top], 6, daySeed()));

  /* ── One genre: a grid, and more of it as you reach the bottom ───────────
   *
   * THE RAIL'S DATA DISCIPLINE, TURNED VERTICAL. The deployed grid paged with
   * its own arithmetic and it was wrong three ways at once, all of them ways
   * the rails (LazyRail's `more()`, Rail's retry clock) already got right:
   *
   *   1. Exhaustion was judged by `next.length >= 50`, but a live FULL page
   *      is routinely 48–49 when Cinemeta drops a row server-side — that is
   *      the entire reason CATALOG_PAGE_SHORT exists. Animation's very first
   *      page maps to 49 rows, so the grid stopped at 49 tiles and printed
   *      "that is everything" while the rail beside it paged to 2,721.
   *   2. A rejected fetch was recorded as the end of the category, for ever.
   *      A bad moment on a train is not the bottom of Documentary.
   *   3. `skip` was the DEDUPED length, so after any short page every deeper
   *      request left the 50-stride — off the rails' shared six-hour cache,
   *      overlapping windows for nothing.
   *
   * Now the grid turns pages exactly the way a rail does: `catalogDeep` one
   * page deeper per approach (cached pages re-walk free, so each approach
   * costs the endpoint one request, shared with any rail on the same query),
   * `drained` only when the catalogue answers short of the ask, and a
   * rejection cools off (MORE_RETRY_MS) and asks again — with a timer, so a
   * reader parked motionless at the bottom is not waiting on a wiggle.
   * Unlike a rail it does NOT loop: a vertical page simply ends.
   */

  let page = $state<CatalogItem[]>([]);
  /** The catalogue answered short of the ask: the category's honest end. */
  let drained = $state(false);
  let sentinel = $state<HTMLDivElement | null>(null);
  /** Single-flight: one `catalogDeep` in the air, never a burst. */
  let loadingMore = false;
  /** After a rejected ask, no re-ask before this — see MORE_RETRY_MS. */
  let coolUntil = 0;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;

  // The first 50 of a genre ARE the `top` request above; the grid starts from it
  // rather than asking the same question twice.
  $effect(() => {
    const first = top;
    if (!genre) return;
    page = first;
    // A FIRST page already short is the whole category — judged by the same
    // 40-row line the rails use, never by `>= 50` (see the header above).
    drained = first.length > 0 && first.length < CATALOG_PAGE_SHORT;
    // A new shelf owes nothing to the old one's failures.
    coolUntil = 0;
    if (retryTimer !== null) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
  });

  $effect(() => {
    return () => {
      if (retryTimer !== null) clearTimeout(retryTimer);
    };
  });

  /** True when the sentinel is within a lookahead of the viewport's bottom. */
  function nearBottom(node: HTMLElement): boolean {
    return node.getBoundingClientRect().top < (window.innerHeight || 0) + 800;
  }

  /**
   * A rejected ask retries on its own clock: a reader parked motionless at
   * the bottom fires no intersection change, and without this the ghosts
   * would shimmer until a scroll the reader has no reason to make.
   */
  function scheduleRetry(): void {
    if (retryTimer !== null) clearTimeout(retryTimer);
    retryTimer = setTimeout(() => {
      retryTimer = null;
      const node = sentinel;
      if (node && nearBottom(node)) void loadMore();
    }, MORE_RETRY_MS);
  }

  async function loadMore(): Promise<void> {
    const g = genre;
    const k = kind;
    if (!g || loadingMore || drained) return;
    if (Date.now() < coolUntil) return;
    if (page.length === 0) return; // the head request seeds the grid first
    loadingMore = true;
    try {
      // One page deeper per approach, on the rails' own 50-stride. The walk
      // over already-fetched pages is cache hits (the same six-hour getJson
      // path), so this costs the endpoint exactly one new request.
      const want = page.length + CATALOG_PAGE;
      const found = await catalogDeep({ kind: k, catalog: 'top', genre: g }, want);
      if (genre !== g || kind !== k) return; // the shelf moved on mid-flight
      coolUntil = 0;
      if (found.length < want) drained = true;
      if (found.length > page.length) page = found;
    } catch {
      // Transient by definition — nothing is marked exhausted. Cool off, keep
      // the ghosts holding the space, ask again.
      coolUntil = Date.now() + MORE_RETRY_MS;
      scheduleRetry();
    } finally {
      loadingMore = false;
    }
  }

  $effect(() => {
    const node = sentinel;
    /*
     * `page.length` is read on purpose. An IntersectionObserver fires on a CHANGE
     * of intersection, and after a page is appended the sentinel is very often
     * still on screen — so without re-observing, pagination stops dead after the
     * second page. Re-creating the observer re-delivers the current state.
     */
    const grown = page.length;
    if (!node || !genre || drained || grown < 0) return;
    if (typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) void loadMore();
      },
      { rootMargin: '800px 0px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  });

  function pickGenre(next: string | null): void {
    go(browseHref(kind, next));
  }

  /**
   * Bring the chosen genre into view.
   *
   * There are 22 chips and the row is 390px wide, so arriving at `/movies/Western`
   * from a link showed a row in which nothing looked selected — the selected chip
   * was 900px off to the right. `nearest` so it does not scroll a chip that is
   * already visible, and `instant` because this is not a gesture the user made.
   */
  let track = $state<HTMLDivElement | null>(null);
  $effect(() => {
    const node = track;
    const chosen = genre;
    if (!node) return;
    const chip = node.querySelector<HTMLElement>('[aria-pressed="true"]');
    if (!chip || chosen === null) return;
    chip.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'instant' });
  });

  const ghosts = Array.from({ length: 12 }, (_, i) => i);
  const valid = $derived(genre === null || isGenre(kind, genre));

  /*
   * The genre row is pinned under the top bar, so it blocks the rails exactly
   * the way the bar's pills do — and it answers the same way: the shell's one
   * scroll boolean (scrolled.svelte.ts, rAF-coalesced, never on a TV) flips
   * `away` on the row and the chips drop to the same `--nav-*-away` glass at
   * the same moment as the bar above them. One system, one clock.
   */
  $effect(() => trackScrolled());
</script>

<svelte:head>
  <title>{genre ? `${genre} ${heading}` : heading} — Jovii</title>
</svelte:head>

{#if !valid}
  <div class="pad">
    <Empty
      icon="grid"
      title="No genre called “{genre}”"
      detail="The catalogue has a fixed list and this is not on it."
    >
      <Chip interactive onclick={() => pickGenre(null)}>All {heading.toLowerCase()}</Chip>
    </Empty>
  </div>
{:else}
  <Hero items={featured} />

  <nav class="genres" class:away={scrolled.away} aria-label="Genres">
    <!-- A row of chips is a rail with no pictures in it: Left/Right walk the
         genres, Up/Down leave for the banner above and the shelf below. -->
    <div class="track" data-dpad="row" bind:this={track}>
      <Chip interactive selected={genre === null} onclick={() => pickGenre(null)}>All</Chip>
      {#each genres as name (name)}
        <Chip interactive selected={genre === name} onclick={() => pickGenre(name)}>{name}</Chip>
      {/each}
    </div>
  </nav>

  {#if genre}
    <section class="grid-wrap">
      <h2>{genre}</h2>
      {#if page.length === 0 && !topFailed && !topAnswered}
        <div class="grid">
          {#each ghosts as n (n)}
            <div class="cell">
              <Skeleton ratio="2 / 3" />
              <Skeleton width="85%" height="0.72rem" radius="5px" />
            </div>
          {/each}
        </div>
      {:else if page.length === 0 && !topFailed}
        <!-- Cinemeta ANSWERED, and the answer was nobody home. An empty shelf
             is a fact, not a loading state — the skeletons used to shimmer
             here for ever. Distinct from the failure branch below, which
             keeps its retry: nothing here would change on a second ask. -->
        <Empty
          icon="grid"
          title="Nothing in {genre} yet"
          detail="Cinemeta answered, and its {genre} shelf is empty for {heading.toLowerCase()}. Another genre will have more to show."
        >
          <Chip interactive onclick={() => pickGenre(null)}>All {heading.toLowerCase()}</Chip>
        </Empty>
      {:else if topFailed}
        <Empty
          icon="alert"
          tone="bad"
          title="The catalogue is not answering"
          detail="Cinemeta could not be reached. Try again in a moment."
        >
          <Button variant="primary" onclick={() => (attempt += 1)}>Try again</Button>
        </Empty>
      {:else}
        <!-- TileGrid: the rail's focus/strip/tap manners on a wrapping grid. -->
        <TileGrid>
          {#each page as item, i (item.id)}
            <!-- This whole shelf is one kind (/shows or /movies), so no tile
                 repeats it with a badge — see Poster's `tagKind`. -->
            <Poster {item} eager={i < 6} tagKind={false} />
          {/each}
          {#if !drained}
            <!-- Ghost tiles hold the next page's space, the rail's shimmer
                 turned vertical: the grid's bottom edge never jumps when a
                 page lands, and a rejected fetch leaves them standing (they
                 are the retry's promise, not a spinner's apology). Poster
                 ghosts are aria-hidden and never focusable, so the d-pad
                 walks past them to wherever the real tiles end. -->
            {#each ghosts as n (n)}
              <Poster ghost />
            {/each}
          {/if}
        </TileGrid>
        <div class="more" bind:this={sentinel}>
          {#if drained}
            <p class="end">That is everything Cinemeta has under {genre}.</p>
          {/if}
        </div>
      {/if}
    </section>
  {:else if topFailed}
    <!--
      THE SHELF HAD NO FAILURE STATE AT ALL, and its rails are lazy — so a
      catalogue that could not be reached left twelve skeletons animating for ever
      with no message. Measured: eight seconds after the last request failed,
      /movies still drew 3 spinners, 0 rails, 0 posters and nothing that said why.
      A page that looks like it is still loading is worse than one that admits it
      is not, because the reader waits instead of retrying.

      `topFailed` is the right signal: the shelf's own popular request is the same
      call every rail below makes, to the same host, so if it could not be reached
      neither can they.
    -->
    <div class="pad">
      <Empty
        icon="alert"
        tone="bad"
        title="The catalogue is not answering"
        detail="Cinemeta could not be reached, so there is nothing to show here. Search still works if it comes back."
      >
        <Button variant="primary" onclick={() => (attempt += 1)}>Try again</Button>
      </Empty>
    </div>
  {:else}
    <!--
      POPULAR → NEW → BY GENRE, from `browseRails()` so this screen and Home are
      built out of the same vocabulary. Deliberately NOT seeded: Home is the room
      you wander into, this is the shelf you come to looking for something, and a
      shelf whose order moves overnight is a shelf you cannot learn.
    -->
    {#each browseRails(kind) as rail, i (rail.key)}
      <LazyRail
        title={rail.title}
        kind={rail.kind}
        catalog={rail.catalog}
        genre={rail.genre}
        eager={i === 0}
      />
    {/each}

    <!--
      The two ways into the rest of it, and on THIS screen both are scoped to
      the kind you are already looking at: the Shows page offers collections
      with a real series shelf and series genres, and never quietly sends you
      to a film. Tiles carry the same generated stills Home uses.
    -->
    <PlateRail
      title="Browse by collection"
      plates={COLLECTIONS.filter((c) => c.kinds.includes(kind)).map((c) => ({
        href: collectionHref(c.slug),
        label: c.label,
        note: c.note,
        image: collectionArt(c.slug)
      }))}
    />

    <PlateRail
      title={kind === 'series' ? 'Series by genre' : 'Films by genre'}
      plates={genresFor(kind).map((g) => ({ href: browseHref(kind, g), label: g, image: genreArt(g) }))}
    />
  {/if}
{/if}

<style>
  .pad {
    padding: var(--space-5) var(--page-pad);
  }

  /* Sticky under the top bar on a desktop, so switching genre never means
     scrolling back up. On a phone the bar scrolls away, so this does too.

     NO GROUND OF ITS OWN. The row used to draw a --bg plate on a ::before and
     a row-wide backdrop blur; the owner's QA killed both ("the categories and
     genres should only be the buttons — no header background whatsoever").
     Like the bar above it, the row is nothing but its pills now: artwork runs
     untouched between and around the chips in every state, and the blur that
     kept the strip readable lives on EACH CHIP instead (see .track .chip). */
  .genres {
    position: sticky;
    top: calc(var(--chrome-overlap));
    z-index: var(--z-rail-edge);
    padding-block: var(--space-2);
  }

  /*
   * THE SCROLLED REGISTER — the top bar's `.away` treatment, same tokens, same
   * clock (TopBar.svelte carries the full argument). The chips drop from their
   * opaque surface fills to the thin nav glass — labels step up from
   * --text-dim to --text with the away ink shadow, the rule every pill over
   * unpredictable artwork already follows; each chip's own backdrop blur does
   * the rest of the carrying, never a row-level one. Hover or focus firms THAT
   * chip back to the firm nav register, exactly like a pill in the bar above.
   * A television never gets the class: `trackScrolled` refuses to run there,
   * the row is not sticky there anyway, and app.css re-pins the away tokens
   * under data-tv.
   */
  .genres.away .track :global(.chip) {
    background: var(--nav-fill-away);
    border-color: var(--nav-line-away);
    color: var(--text);
    text-shadow: var(--nav-ink-shadow-away);
  }
  .genres.away .track :global(.chip.selected) {
    background: var(--nav-fill-here-away);
    border-color: var(--nav-line-here-away);
  }
  .genres.away .track :global(.chip:hover:not(:disabled)),
  .genres.away .track :global(.chip:focus-visible) {
    background: var(--nav-fill-hover-away);
    border-color: var(--nav-line);
    color: var(--text);
    text-shadow: var(--nav-ink-shadow);
  }
  .genres.away .track :global(.chip.selected:hover),
  .genres.away .track :global(.chip.selected:focus-visible) {
    background: var(--nav-fill-here);
    border-color: var(--nav-line-here);
  }
  /*
   * NOT STICKY ON A TELEVISION. A pinned filter row is a pointer idiom —
   * it saves a mouse the scroll back up. To a remote it is a trap: pinned, it
   * is permanently "just above" whatever rail the focus is on, so every second
   * press of Up landed on a genre chip instead of the previous rail (measured
   * on /shows at 1280x720: tile -> History -> tile -> History, all the way
   * up). Left in the document's own order, Up walks rail to rail and reaches
   * the chips exactly where they live — under the banner — and every scroll on
   * a TV is instant, so getting back to them costs nothing.
   */
  :global(html[data-tv='on']) .genres {
    /* `relative`, not `static`: everything the paragraph above wants is "not
       sticky, left in the document's own order", and relative is exactly that
       — while staying a positioned box, so the row's `z-index` keeps holding.
       `top` must be re-zeroed: sticky reads it as a stick offset, relative as
       a visual shove — left alone it drew the row 85px low, over the grid's
       heading (measured). */
    position: relative;
    top: auto;
  }
  .track {
    display: flex;
    gap: var(--space-2);
    padding-inline: max(var(--page-pad), var(--safe-l)) max(var(--page-pad), var(--safe-r));
    overflow-x: auto;
    overscroll-behavior-x: contain;
    scrollbar-width: none;
    /*
     * ROOM FOR THE RING. `overflow-x: auto` makes the vertical axis clip too
     * (a scroll container clips both ways), and the focus outline sits 2px off
     * the chip with 2px of width — on a TV it was drawn with its top and
     * bottom edges cut clean off. Block padding gives the ring paint room
     * inside the clip; the negative margin hands the height straight back so
     * the pinned row measures exactly what it did.
     */
    padding-block: 6px;
    margin-block: -6px;
  }
  .track::-webkit-scrollbar {
    display: none;
  }
  .track :global(.chip) {
    min-height: 36px;
    padding-inline: var(--space-3);
    font-size: var(--text-sm);
    /*
     * THE CHIP'S OWN GLASS — the bar's pill treatment, one control at a time
     * (TopBar's `ul a` carries the same pair of lines). With no row plate
     * behind it, this per-chip blur is what keeps the thinned away fill
     * readable over artwork. Declared constant so there is nothing to
     * interpolate: over the flat ground at rest it is a visual no-op, and the
     * TV tier's global `backdrop-filter: none !important` still kills it.
     */
    backdrop-filter: var(--glass-blur);
    /* The away crossfade rides the same clock as the bar's pills; text-shadow
       joins the list so the ink scrim fades rather than snapping. */
    transition:
      background-color var(--dur) var(--ease),
      border-color var(--dur) var(--ease),
      color var(--dur) var(--ease),
      text-shadow var(--dur) var(--ease);
  }
  /*
   * THE REMOTE'S RING, THIS ROW ONLY. `html[data-dpad='on']` widens the
   * app-wide focus outline to 3px at 3px offset for the sofa; on these small
   * lozenges that read as a fat white border nothing like the same row on an
   * iPad — the owner asked for the touch look back. 2px at 2px is the ring
   * every desktop pointer already gets, and the selected chip still carries
   * its luminance fill, so "where am I" survives the trim. Scoped to the
   * genre track: tiles and every other TV ring keep their 3px.
   */
  :global(html[data-dpad='on']) .track :global(.chip:focus-visible) {
    outline-width: 2px;
    outline-offset: 2px;
  }
  /*
   * Defect #16: a 27px chip is not a touch target, and these are the browse
   * screen's primary control. A mouse does not need 44px and a phone does, so the
   * floor is applied where it is earned rather than everywhere.
   */
  @media (pointer: coarse), (max-width: 767px) {
    .track :global(.chip) {
      min-height: var(--hit);
      padding-inline: var(--space-4);
    }
  }

  .grid-wrap {
    padding: var(--space-4) max(var(--page-pad), var(--safe-r)) var(--space-6)
      max(var(--page-pad), var(--safe-l));
  }
  h2 {
    margin-bottom: var(--space-3);
    font-size: var(--text-lg);
    font-weight: var(--weight-semi);
    letter-spacing: var(--tracking-snug);
  }

  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(var(--tile-min), 1fr));
    gap: 18px var(--gap);
  }
  .cell {
    display: grid;
    gap: 7px;
  }

  .more {
    display: grid;
    place-items: center;
    min-height: 60px;
    margin-top: var(--space-4);
  }
  .more p {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    color: var(--text-faint);
    font-size: var(--text-xs);
  }
  .end {
    text-align: center;
  }

</style>
